import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { EmptyState, PrestIcon } from "@/components/common";
import { PRESTATIONS, fmtNum } from "@/lib/helpers";

export default function Stats() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/stats/prestations").then((r) => setData(r.data)).catch(() => setData({ prestations: [], total_visits: 0 }));
  }, []);

  const maxVisits = Math.max(1, ...(data?.prestations || []).map((p) => p.visits));

  return (
    <div className="space-y-6" data-testid="stats-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 lg:text-3xl">Statistiques</h1>
        <p className="text-sm text-slate-500">Prestations les plus demandées et surfaces relevées, à partir de vos visites.</p>
      </div>

      {!data ? (
        <div className="p-10 text-center text-slate-400">Chargement…</div>
      ) : data.prestations.length === 0 ? (
        <EmptyState icon="BarChart3" title="Aucune donnée" testId="stats-empty">
          Créez des visites avec des relevés pour voir apparaître les statistiques.
        </EmptyState>
      ) : (
        <>
          <Card className="p-5">
            <h2 className="mb-4 font-display text-lg font-semibold text-slate-800">Prestations les plus demandées</h2>
            <div className="space-y-3">
              {data.prestations.map((p) => {
                const icon = PRESTATIONS[p.prestation]?.icon || "Square";
                return (
                  <div key={p.prestation} data-testid={`stat-row-${p.prestation}`}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 font-medium text-slate-700">
                        <PrestIcon name={icon} className="h-4 w-4 text-emerald-600" /> {p.label}
                      </span>
                      <span className="font-mono-data text-slate-500">{p.visits} visite(s) · {p.zones} zone(s)</span>
                    </div>
                    <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${(p.visits / maxVisits) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="mb-4 font-display text-lg font-semibold text-slate-800">Quantités relevées par prestation</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.prestations.map((p) => (
                <div key={p.prestation} className="rounded-xl border border-slate-200 p-4" data-testid={`stat-qty-${p.prestation}`}>
                  <div className="mb-2 flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                      <PrestIcon name={PRESTATIONS[p.prestation]?.icon || "Square"} className="h-5 w-5" />
                    </div>
                    <span className="font-semibold text-slate-800">{p.label}</span>
                  </div>
                  {Object.keys(p.totals).length === 0 ? (
                    <p className="text-sm text-slate-400">Aucune quantité</p>
                  ) : (
                    <ul className="space-y-1">
                      {Object.entries(p.totals).map(([unit, t]) => (
                        <li key={unit} className="flex items-baseline justify-between text-sm">
                          <span className="text-slate-500">{unit}</span>
                          <span className="font-mono-data font-medium text-slate-800">
                            {fmtNum(t.value)}
                            {t.estimee > 0 && <span className="text-amber-600"> (dont {fmtNum(t.estimee)} est.)</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-400">Les m², mètres linéaires et unités ne sont jamais additionnés entre eux.</p>
          </Card>
        </>
      )}
    </div>
  );
}
